/**
 * L'heure d'ouverture des inscriptions, dite dans le bon fuseau.
 *
 * ## Le défaut, mesuré le 8 septembre 2026
 *
 * Le champ « Visible à partir de » de l'ancien formulaire était un
 * `<input type="datetime-local">` — qui rend une heure de MUR, sans fuseau :
 * `"2026-05-30T09:00"`. Cette chaîne partait telle quelle dans une colonne
 * `timestamptz`, et PostgreSQL, dont la session tourne en UTC, la lisait comme
 * 09:00 UTC.
 *
 * Les valeurs en base le disent : les 76 sessions de l'été 2026 portent des
 * ouvertures à `09:20:00+00`, `09:22:00+00`, ... `09:37:00+00`. Soit **11h20 à
 * 11h37 à Bruxelles**. Deux heures après l'heure annoncée aux familles.
 *
 * Et l'aller-retour était perdant dans les deux sens : à la modification,
 * `datetime-local` refuse `"2026-05-30 09:20:00+00"` — le champ s'affichait
 * **vide**, donc l'administration ne pouvait pas relire une ouverture déjà
 * fixée sans aller dans la base.
 *
 * ## Pourquoi `Intl` et pas une bibliothèque
 *
 * Le décalage de Bruxelles n'est pas constant : +1 en hiver, +2 en été. Un
 * décalage écrit en dur serait juste une moitié de l'année — et l'ouverture des
 * inscriptions d'été se décide justement fin mai, en heure d'été, alors que
 * celle des stages d'hiver se décide en novembre.
 *
 * `Intl.DateTimeFormat` connaît la base de données des fuseaux du navigateur.
 * Aucune dépendance à ajouter, et la règle suit les changements d'heure sans
 * qu'on les inscrive.
 */

/** Le fuseau de l'association. Les centres sont tous à Bruxelles. */
export const FUSEAU = 'Europe/Brussels';

/**
 * Le décalage d'un fuseau, en minutes, à un instant donné.
 *
 * Positif à l'est de Greenwich : +120 pour Bruxelles en été.
 *
 * La méthode est celle qu'impose `Intl` : on demande au fuseau comment il
 * ÉCRIRAIT cet instant, on relit cette écriture comme si elle était en UTC, et
 * l'écart entre les deux est le décalage.
 */
export function decalageMinutes(instant: Date, fuseau: string = FUSEAU): number {
  const parties = new Intl.DateTimeFormat('en-US', {
    timeZone: fuseau,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const lire = (type: string) =>
    Number(parties.find((p) => p.type === type)?.value ?? '0');

  /*
    `hour12: false` rend 24 pour minuit sur certaines implémentations, là où
    `Date.UTC` attend 0. Sans ce repli, une ouverture fixée à minuit dérivait
    d'un jour entier.
  */
  const heure = lire('hour') % 24;

  const commeSiUTC = Date.UTC(
    lire('year'),
    lire('month') - 1,
    lire('day'),
    heure,
    lire('minute'),
    lire('second'),
  );

  return Math.round((commeSiUTC - instant.getTime()) / 60000);
}

/**
 * Une heure de mur — ce que tape l'administration — vers l'instant réel.
 *
 * Rend une chaîne ISO en UTC, prête pour une colonne `timestamptz`.
 *
 * `"2026-05-30T09:00"` à Bruxelles rend `"2026-05-30T07:00:00.000Z"` : c'est
 * bien 9h du matin à Bruxelles, et non 9h UTC comme l'écrivait l'ancien
 * formulaire.
 *
 * Rend `null` sur une saisie vide ou illisible : un champ laissé vide veut dire
 * « visible tout de suite », et ne doit pas devenir une date inventée.
 */
export function versInstant(
  heureDeMur: string | null | undefined,
  fuseau: string = FUSEAU,
): string | null {
  if (!heureDeMur) return null;

  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(heureDeMur);
  if (!m) return null;

  const [, a, mo, j, h, mi, s] = m;
  const mur = Date.UTC(+a, +mo - 1, +j, +h, +mi, s ? +s : 0);

  /*
    Deux passes, et la seconde n'est pas du zèle.

    Pour trouver l'instant, il faut le décalage ; mais pour connaître le
    décalage, il faut l'instant. On part donc de l'heure de mur lue comme de
    l'UTC — faux de une à deux heures, jamais plus — puis on corrige.

    La seconde passe rattrape les saisies proches d'un changement d'heure, où
    la première estimation tombe du mauvais côté de la bascule.
  */
  const premier = decalageMinutes(new Date(mur), fuseau);
  let instant = mur - premier * 60000;

  const second = decalageMinutes(new Date(instant), fuseau);
  if (second !== premier) {
    instant = mur - second * 60000;
    /*
      Sur l'heure inexistante du passage à l'heure d'été (2h30 le dernier
      dimanche de mars n'existe pas à Bruxelles), les deux passes se renvoient
      la balle. On garde alors la première estimation : elle tombe sur l'heure
      juste après le saut, ce qui est le choix le moins surprenant — une
      ouverture annoncée à 2h30 ouvre à 3h00 plutôt que la veille.
    */
    if (decalageMinutes(new Date(instant), fuseau) !== second) {
      instant = mur - premier * 60000;
    }
  }

  return new Date(instant).toISOString();
}

/**
 * L'inverse : un instant vers l'heure de mur que `datetime-local` sait afficher.
 *
 * Rend `"YYYY-MM-DDTHH:mm"`, le seul format que le champ accepte. C'est ce qui
 * manquait pour qu'une ouverture déjà fixée soit relisible : le champ recevait
 * `"2026-05-30 09:20:00+00"` et s'affichait vide.
 */
export function versHeureDeMur(
  instant: string | Date | null | undefined,
  fuseau: string = FUSEAU,
): string {
  if (!instant) return '';

  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) return '';

  const parties = new Intl.DateTimeFormat('en-CA', {
    timeZone: fuseau,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(d);

  const lire = (type: string) => parties.find((p) => p.type === type)?.value ?? '';
  const heure = String(Number(lire('hour')) % 24).padStart(2, '0');

  return `${lire('year')}-${lire('month')}-${lire('day')}T${heure}:${lire('minute')}`;
}

/**
 * Les heures auxquelles une vague d'inscriptions s'ouvre réellement.
 *
 * > « On ne lancera jamais de stages à 9:53 par exemple, ce sera toujours des
 * >   heures rondes, 09:00 ou 10:00. »
 *
 * La base le confirme : sur les 45 sessions de l'automne 2026, les ouvertures
 * voulues sont 09:00 et 10:00. Les valeurs bancales qui traînent —
 * `21:17`, et les `09:20`…`09:37` de l'été — ne sont pas des choix, ce sont
 * les traces d'un champ `datetime-local` où la minute se tape à la main.
 *
 * Une liste fermée supprime la classe d'erreur entière. De 6h à 22h : avant
 * 6h personne ne se lève pour une inscription, après 22h non plus.
 */
export const HEURES_RONDES: readonly string[] = Array.from(
  { length: 17 },
  (_, i) => `${String(i + 6).padStart(2, '0')}:00`,
);

/**
 * Découpe une heure de mur en le jour et l'heure que deux champs séparés
 * savent porter.
 *
 * Rend `{ jour: '', heure: '' }` sur une entrée vide — ce qui veut dire
 * « visible dès la publication », et doit le rester.
 */
export function decouperHeureDeMur(mur: string | null | undefined): {
  jour: string;
  heure: string;
} {
  if (!mur) return { jour: '', heure: '' };
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(mur);
  if (!m) return { jour: '', heure: '' };
  return { jour: m[1], heure: m[2] };
}

/**
 * L'inverse : deux champs vers l'heure de mur que `versInstant` attend.
 *
 * Sans jour, il n'y a pas d'ouverture — une heure seule ne veut rien dire, et
 * rendre `''` la fait retomber sur « visible tout de suite », ce qui est le
 * comportement demandé pour un champ laissé vide.
 *
 * Sans heure mais avec un jour, on prend minuit : l'administration a désigné
 * un jour, et ouvrir au début de ce jour est la lecture la moins surprenante.
 */
export function assemblerHeureDeMur(jour: string, heure: string): string {
  if (!jour) return '';
  return `${jour}T${heure || '00:00'}`;
}

/**
 * Les heures à proposer, l'existante comprise même si elle n'est pas ronde.
 *
 * Une liste fermée qui avalerait une valeur hors liste serait pire que le
 * champ libre : l'écran afficherait 09:00 là où la base porte 09:37, et la
 * prochaine correction écraserait l'heure réelle sans que personne l'ait
 * demandé. On l'ajoute donc, et le rendu la signale.
 */
export function heuresProposees(heureActuelle: string): readonly string[] {
  if (!heureActuelle || HEURES_RONDES.includes(heureActuelle)) return HEURES_RONDES;
  return [...HEURES_RONDES, heureActuelle].sort();
}

/**
 * L'ouverture, telle qu'on la montre à l'écran.
 *
 * Dit toujours le fuseau en clair. Le défaut mesuré n'était pas seulement un
 * mauvais calcul : c'était qu'aucun écran ne disait dans quel fuseau il
 * parlait, donc personne ne pouvait s'apercevoir de l'écart de deux heures.
 */
export function phraseDOuverture(
  instant: string | Date | null | undefined,
  fuseau: string = FUSEAU,
): string {
  if (!instant) return 'Visible dès la publication';

  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) return 'Date d’ouverture illisible';

  const texte = new Intl.DateTimeFormat('fr-BE', {
    timeZone: fuseau,
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(d);

  return `À partir du ${texte} (heure de Bruxelles)`;
}

/**
 * Vrai si l'ouverture est déjà passée — donc si la session est visible.
 *
 * `maintenant` est un paramètre pour que le test n'ait pas à voyager dans le
 * temps.
 */
export function dejaOuvert(
  instant: string | Date | null | undefined,
  maintenant: Date = new Date(),
): boolean {
  if (!instant) return true;
  const d = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(d.getTime())) return true;
  return d.getTime() <= maintenant.getTime();
}
